<template>
    <div
        class="EntitySearchAndFilter"
        :class="{
            'EntitySearchAndFilter--open':opened,
            'EntitySearchAndFilter--active': hasFilters,
        }"
    >
        <Popover
            :open="opened"
            @hide="closeFilterPopover"
        >
            <template #base>
                <div class="EntitySearchAndFilter__container">
                    <div class="EntitySearchAndFilter__prefix">
                        <EidIcon :icon-src="faMagnifyingGlass" />
                    </div>
                    <input
                        class="EntitySearchAndFilter__embeddedSearch"
                        type="text"
                        placeholder="Search..."
                        :value="search"
                        @input="scheduleChange"
                    >
                    <div class="EntitySearchAndFilter__postfix" @click="toggleFilterPopover">
                        <span>Advanced</span>
                        <FontAwesomeIcon :icon="faAngleDown" />
                    </div>
                </div>
            </template>

            <EntityFiltersPopover
                :filters="filters"
                :custom-properties="customProperties"
                :risk-profiles="riskProfiles"
                :effective-risk-profiles="effectiveRiskProfiles"
                :collateral-custom-property-key="collateralCustomPropertyKey"
                :custom-property-collaterals="customPropertyCollaterals"
                @close="closeFilterPopover"
                @submit="applyFilters"
            />
        </Popover>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import isEqual from 'lodash/isEqual';
    import pickBy from 'lodash/pickBy';

    import { faAngleDown, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import Popover from '@evidentid/dashboard-commons/components/Popover/Popover.vue';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import {
        EffectiveRiskProfiles,
    } from '@evidentid/tprm-portal-lib/models/decisioning/EffectiveRiskProfiles.model';
    import {
        RiskProfile,
    } from '@evidentid/tprm-portal-lib/models/decisioning/RiskProfile.model';

    import EntityFiltersPopover
        from '@/modules/entity-filtering/components/EntityFiltersPopover/EntityFiltersPopover.vue';
    import { EntityFilters } from '@/modules/entity-filtering/types';

    export default defineComponent({
        name: 'EntitySearchAndFilter',
        components: {
            Popover,
            FontAwesomeIcon,
            EidIcon,
            EntityFiltersPopover,
        },
        props: {
            debounceTime: {
                type: Number as PropType<number>,
                default: 1000,
            },
            disabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            filters: {
                type: Object as PropType<EntityFilters>,
                default: (): EntityFilters => ({}) as EntityFilters,
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [] as CustomProperty[],
            },
            riskProfiles: {
                type: Array as PropType<RiskProfile[]>,
                default: (): RiskProfile[] => [] as RiskProfile[],
            },
            effectiveRiskProfiles: {
                type: Array as PropType<EffectiveRiskProfiles[]>,
                default: (): EffectiveRiskProfiles[] => [] as EffectiveRiskProfiles[],
            },
            collateralCustomPropertyKey: {
                type: String as PropType<string>,
                default: '',
            },
            customPropertyCollaterals: {
                type: Object as PropType<CustomProperty | null | undefined>,
                default: undefined,
            },
        },
        emits: [ 'change' ],
        data() {
            return {
                search: '',
                faMagnifyingGlass,
                faAngleDown,
                isMounted: false,
                debounceTimeout: undefined as undefined | number,
                opened: false,
            };
        },
        computed: {
            hasFilters(): boolean {
                return Object.keys(this.filters).length > 0;
            },
        },
        watch: {
            filters: {
                immediate: true,
                handler(newValue: EntityFilters) {
                    this.search = newValue.search || '';
                    this.abortScheduledAction();
                },
            },
        },
        mounted() {
            this.isMounted = true;
        },
        unmounted() {
            this.isMounted = false;
        },
        methods: {
            scheduleChange(inputEvent: InputEvent): void {
                this.search = (inputEvent.target as HTMLInputElement).value;
                this.abortScheduledAction();
                this.debounceTimeout = setTimeout(
                    this.immediateChange.bind(this),
                    this.debounceTime,
                ) as unknown as number;
            },
            immediateChange(): void {
                this.abortScheduledAction();
                if (this.isMounted && !isEqual(this.search, this.filters.search)) {
                    this.$emit('change', pickBy({ ...this.filters, search: this.search }, Boolean));
                }
            },
            abortScheduledAction() {
                clearTimeout(this.debounceTimeout);
            },
            closeFilterPopover() {
                this.opened = false;
            },
            toggleFilterPopover(event: Event) {
                this.opened = !this.opened;
                if (this.opened) {
                    event.stopPropagation();
                }
            },
            applyFilters(filters: EntityFilters) {
                this.$emit('change', pickBy({
                    ...filters,
                }, Boolean));
                this.closeFilterPopover();
            },
        },
    });
</script>
